import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { useCurrency } from '../../context/CurrencyContext';
import { useProducts } from '../../context/ProductContext';
import ProductCard from '../shop/ProductCard';
import { fadeIn } from '../../utils/gsapAnimations';
import SectionLoader from '../common/SectionLoader';

function FeaturedCollections({
  title = 'Featured Collections',
  eyebrow = null,
  description = null,
  limit = 4,
}) {
  const sectionRef = useRef(null);
  const { formatPrice } = useCurrency();
  const { products, loading, error } = useProducts();

  useEffect(() => {
    fadeIn(sectionRef.current, 0.3);
  }, []);

  const featured = useMemo(() => {
    if (!Array.isArray(products)) return [];
    return [...products].sort(() => 0.5 - Math.random()).slice(0, limit);
  }, [products, limit]);

  if (loading) {
    return <SectionLoader message="Loading featured collections..." />;
  }

  if (error) {
    return (
      <div className="text-center py-10 text-red-500 font-montserrat">
        {error}
      </div>
    );
  }

  if (featured.length === 0) return null;

  return (
    <div ref={sectionRef} className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1320px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 sm:mb-12">
          <div className="max-w-3xl">
            {eyebrow && (
              <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-montserrat font-semibold uppercase tracking-[0.22em] text-[#557338]">
                <span className="h-px w-8 bg-[#7e9b55]" />
                {eyebrow}
              </div>
            )}
            <h2 className="mt-4 font-playfair-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] text-[#173d2d]">
              {title}
            </h2>
            {description && (
              <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed font-montserrat text-[#617168]">
                {description}
              </p>
            )}
          </div>

          <Link
            to="/shop"
            className="group inline-flex items-center gap-3 text-sm sm:text-base font-montserrat font-semibold text-[#173d2d]"
          >
            View all products
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#173d2d]/15 bg-white transition-all duration-300 group-hover:bg-[#173d2d] group-hover:text-white">
              <FiArrowRight className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {featured.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              formatPrice={formatPrice}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default FeaturedCollections;
